import { afterStartup, isNative } from './platform';
import { PORTAL } from './portal';

/**
 * Google Analytics 4 on the website, when VITE_GA_ID (e.g. "G-ABC123") is set at build time. Not in
 * the apps or portal builds (portals forbid third-party tracking and report their own numbers).
 *
 * Consent: in the EEA, the UK and Switzerland, advertising storage stays off until the visitor agrees
 * in Google's consent message (AdSense → Privacy & messaging), which updates these signals itself.
 */
const GA_ID = (import.meta.env.VITE_GA_ID as string | undefined)?.trim() || '';
const CONSENT_REGIONS = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'CH'];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let started = false;

export function initAnalytics(): void {
  if (started || !GA_ID || PORTAL || isNative) return;
  started = true;
  window.dataLayer = window.dataLayer ?? [];
  // gtag queues its arguments object as-is (that's what the library expects).
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('consent', 'default', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'granted', region: CONSENT_REGIONS });
  window.gtag('js', new Date());
  // Page views are sent by trackPage on every route change (this is a single-page app).
  window.gtag('config', GA_ID, { send_page_view: false });
  // The library is the biggest download on the page (~530 kB unpacked): fetch it once the first
  // screen is up. Calls made before then wait in dataLayer and are sent when it arrives.
  afterStartup(() => {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
    document.head.appendChild(s);
  });
}

export function trackPage(path: string): void {
  if (!started) return;
  window.gtag?.('event', 'page_view', { page_path: path, page_location: window.location.origin + path, page_title: document.title });
}

/** A named moment worth counting (e.g. a game starting). Keep names short and snake_case. */
export function trackEvent(name: string, params: Record<string, string | number> = {}): void {
  if (!started) return;
  window.gtag?.('event', name, params);
}

const GAMES_KEY = 'snakefeast.gamesStarted';

/**
 * Count a game started on this device (online, practice or solo) and say which one it is, bucketed
 * ("1".."5", "6-10", "11+"), so the funnel shows how many people get to a second and third game.
 */
export function nextGameNumber(): string {
  let n = 1;
  try {
    n = (Number(localStorage.getItem(GAMES_KEY)) || 0) + 1;
    localStorage.setItem(GAMES_KEY, String(n));
  } catch {
    /* private mode: every game counts as a first */
  }
  return n <= 5 ? String(n) : n <= 10 ? '6-10' : '11+';
}
